"use client";

import { Heart } from "lucide-react";

import { cn } from "@/utils/cn";

type WishlistHeartButtonProps = {
  courseSlug: string;
  isWished: boolean;
  size?: "sm" | "md";
  variant?: "overlay" | "inline";
  disabled?: boolean;
  addAriaLabel?: string;
  removeAriaLabel?: string;
  onToggle?: (courseSlug: string) => void;
  className?: string;
};

const sizeClassNameByValue = {
  sm: "h-11 w-11",
  md: "h-11 w-11 sm:h-12 sm:w-12",
} satisfies Record<NonNullable<WishlistHeartButtonProps["size"]>, string>;

const iconClassNameByValue = {
  sm: "h-5 w-5",
  md: "h-5 w-5 sm:h-[1.35rem] sm:w-[1.35rem]",
} satisfies Record<NonNullable<WishlistHeartButtonProps["size"]>, string>;

export function WishlistHeartButton({
  courseSlug,
  isWished,
  size = "md",
  variant = "overlay",
  disabled = false,
  addAriaLabel = "Add to wishlist",
  removeAriaLabel = "Remove from wishlist",
  onToggle,
  className,
}: WishlistHeartButtonProps) {
  const ariaLabel = isWished ? removeAriaLabel : addAriaLabel;

  return (
    <button
      type="button"
      aria-pressed={isWished}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggle?.(courseSlug);
      }}
      className={cn(
        "inline-flex items-center justify-center rounded-full border outline-none transition-colors",
        "focus-visible:ring-2 focus-visible:ring-[#0f766e]/30",
        sizeClassNameByValue[size],
        variant === "overlay"
          ? "border-[#d6c8b0] bg-[#fffaf1]/95 text-[#30453f] shadow-[0_6px_16px_rgba(34,47,42,0.16)] hover:border-[#bea88a]"
          : "border-[#cdbb9f] bg-[#fffaf1] text-[#30453f] hover:border-[#b79f79]",
        isWished ? "text-[#9f2f52]" : null,
        disabled ? "cursor-not-allowed opacity-60" : null,
        className
      )}
    >
      <Heart
        className={iconClassNameByValue[size]}
        strokeWidth={1.9}
        fill={isWished ? "currentColor" : "none"}
      />
    </button>
  );
}
